/* eslint-disable no-unused-vars, autofix/no-unused-vars, no-console, autofix/no-console */

import { endpointEnum } from '../services/rest/makeAPIRequest';
import { AxiosRequestConfig } from 'axios';
import Axios from '../common/axiosInterceptor';

/**
 * Level Description (ALL > TRACE > DEBUG > INFO > WARN > ERROR > FATAL > OFF)
 * Set by process.env.VITE_APP_LOG_LEVEL in .env
 * Requires to restart app to force reload of .env
 * =================
 * ALL    - All levels including custom levels.
 * TRACE  - Designates finer-grained informational events than the DEBUG.
 * DEBUG  - Designates fine-grained informational events that are most useful to debug an application.
 * INFO   - Designates informational messages that highlight the progress of the application at coarse-grained level.
 * WARN   - Designates potentially harmful situations.
 * ERROR  - Designates error events that might still allow the application to continue running.
 * FATAL  - Designates very severe error events that will presumably lead the application to abort.
 * OFF    - The highest possible rank and is intended to turn off logging.
 * @todo move to central location and replace with log4j or equivalent
 */
enum logLevelEnum {
  OFF, // The highest possible rank and is intended to turn off logging.
  FATAL, // Designates very severe error events that will presumably lead the application to abort.
  ERROR, // Designates error events that might still allow the application to continue running.
  WARN, // Designates potentially harmful situations.
  INFO, // Designates informational messages that highlight the progress of the application at coarse-grained level.
  DEBUG, // Designates fine-grained informational events that are most useful to debug an application.
  TRACE, // Designates finer-grained informational events than the DEBUG.
  ALL, // All levels including custom levels.
}

enum logLevelNameEnum {
  OFF = 'off', // turn off logging.
  FATAL = 'fatal', // Designates very severe error events that will presumably lead the application to abort.
  ERROR = 'error', // Designates error events that might still allow the application to continue running.
  WARN = 'warn', // Designates potentially harmful situations.
  INFO = 'info', // Designates informational messages that highlight the progress of the application at coarse-grained level.
  DEBUG = 'debug', // Designates fine-grained informational events that are most useful to debug an application.
  TRACE = 'trace', // Designates finer-grained informational events than the DEBUG.
  ALL = 'all', // All levels including custom levels.
}

/**
 * Get caller name via Error stack
 * NOTE: cannot use Function.caller or arguments.caller due to 'use strict'
 * @param index print name found at stack index
 */
function getCallerName(index: number = 3): string | undefined {
  const logLines = new Error().stack?.split('\n');
  const fnNameMatcher = /([^(]+)@|at ([^(]+) \(/;
  const regexResult = fnNameMatcher.exec(logLines?.[index] ?? '');
  return regexResult?.[1] || regexResult?.[2];
}

async function cloudwatchLogger(
  level: logLevelNameEnum,
  message: any,
  callerName?: string
) {
  const request: AxiosRequestConfig = {
    method: 'post',
    url: `${process.env.VITE_APP_API_DOMAIN}/${process.env.VITE_APP_API_STAGE}/${endpointEnum.CLOUDWATCH_LOGGER}`,
    data: {
      level,
      message: typeof message === 'object' ? JSON.stringify(message) : message,
      callerName,
      url: window.location.href,
    },
    headers: {
      'content-type': 'application/json;charset=utf-8',
    },
  };

  try {
    // execute request
    await Axios(request);
  } catch (exception) {
    // record error in console log
    console.error('cloudwatchLogger[ERROR]:', {
      request,
      exception,
    });
  }
}

/**
 * Inspired by log4j. Placeholder for real library.
 * Level Description (ALL > TRACE > DEBUG > INFO > WARN > ERROR > FATAL > OFF)
 * @see https://www.tutorialspoint.com/log4j/log4j_logging_levels.htm
 * @todo replace with log4j library or equivalent
 */
export class Logger {
  /**
   * Get loglevel from process.env.VITE_APP_LOG_LEVEL (OFF if undefined)
   */
  static getLoglevel(): logLevelEnum {
    // cast string to enum and lookup by name
    const logLevelValue: logLevelEnum = (logLevelEnum as any)[
      process.env.VITE_APP_LOG_LEVEL ?? 'OFF'
    ];
    return logLevelValue;
  }

  /**
   * Get loglevel from process.env.VITE_APP_LOG_LEVEL_FOR_SERVER (OFF if undefined)
   */
  static getLoglevelForServer(): logLevelEnum {
    // cast string to enum and lookup by name
    const logLevelValue: logLevelEnum = (logLevelEnum as any)[
      process.env.VITE_APP_LOG_LEVEL_FOR_SERVER ?? 'OFF'
    ];
    return logLevelValue;
  }

  /**
   * Log message based on log-level
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static trace(
    message: any,
    callerName: string | undefined = getCallerName()
  ): void {
    if (Logger.getLoglevelForServer() >= logLevelEnum.TRACE) {
      cloudwatchLogger(logLevelNameEnum.TRACE, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.TRACE) {
      console.groupCollapsed(`${callerName}[TRACE]:`, message);
      console.log(callerName, ':', message);
      console.trace(); // hidden in collapsed group
      console.groupEnd();
    }
  }

  /**
   * Special method used just for testing (logs at same level as debug)
   * NOTE: for testing purposes only, all calls to this method shoudld be removed prior to commit into repo
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static test(
    message: any,
    callerName: string | undefined = getCallerName()
  ): void {
    if (Logger.getLoglevelForServer() >= logLevelEnum.DEBUG) {
      cloudwatchLogger(logLevelNameEnum.DEBUG, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.DEBUG) {
      console.groupCollapsed(`${callerName}[DEBUG|TEST]:`, message);
      console.log(callerName, ':', message);
      console.trace(); // hidden in collapsed group
      console.groupEnd();
    }
  }

  /**
   * Log message based on log-level
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static debug(
    message: any,
    callerName: string | undefined = getCallerName()
  ): void {
    if (Logger.getLoglevelForServer() >= logLevelEnum.DEBUG) {
      cloudwatchLogger(logLevelNameEnum.DEBUG, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.DEBUG) {
      console.groupCollapsed(`${callerName}[DEBUG]:`, message);
      console.log(message);
      console.trace(); // hidden in collapsed group
      console.groupEnd();
    }
  }

  /**
   * Log message based on log-level
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static async info(
    message: any,
    callerName: string | undefined = getCallerName()
  ): Promise<void> {
    if (Logger.getLoglevelForServer() >= logLevelEnum.INFO) {
      cloudwatchLogger(logLevelNameEnum.INFO, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.INFO) {
      console.groupCollapsed(`${callerName}[INFO]:`, message);
      console.info(message);
      console.trace(); // hidden in collapsed group
      console.groupEnd();
    }
  }

  /**
   * Log message based on log-level
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static warn(
    message: any,
    callerName: string | undefined = getCallerName()
  ): void {
    if (Logger.getLoglevelForServer() >= logLevelEnum.WARN) {
      cloudwatchLogger(logLevelNameEnum.WARN, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.WARN) {
      console.warn(`${callerName}[WARN]:`, message);
    }
  }

  /**
   * Log message based on log-level
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static error(
    message: any,
    callerName: string | undefined = getCallerName()
  ): void {
    if (Logger.getLoglevelForServer() >= logLevelEnum.ERROR) {
      cloudwatchLogger(logLevelNameEnum.ERROR, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.ERROR) {
      console.error(`${callerName}[ERROR]:`, message);
    }
  }

  /**
   * Log message based on log-level
   * @param message message to show in log
   * @param callerName caller name override if called from unnamed function
   */
  static fatal(
    message: any,
    callerName: string | undefined = getCallerName()
  ): void {
    if (Logger.getLoglevelForServer() >= logLevelEnum.FATAL) {
      cloudwatchLogger(logLevelNameEnum.FATAL, message, callerName);
    }
    if (Logger.getLoglevel() >= logLevelEnum.FATAL) {
      console.error(`${callerName}[FATAL]:`, message);
    }
  }
}

// run once on application start to print out log-level name
// turn off for npm test
if (process.env.NODE_ENV !== 'test') {
  // eslint-disable-next-line no-console
  console.log(
    `Logger.ts: log-level set at ${logLevelEnum[Logger.getLoglevel()]}`
  );
}
